import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function StoryPage() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-orange-50 text-gray-800 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <motion.div {...fadeIn} className="mb-8">
          <Button variant="ghost" asChild className="mb-6">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft size={20} />
              Back to Home
            </Link>
          </Button>
          
          <h1 className="text-4xl md:text-5xl font-bold text-yellow-800 mb-6">
            Why Intergenerational Storytelling Matters for 2ByaaG
          </h1>
        </motion.div>

        <div className="space-y-8">
          <motion.div {...fadeIn} className="prose max-w-none">
            <Card className="bg-white/80 backdrop-blur shadow-xl">
              <CardContent className="p-6 space-y-4">
                <p className="text-lg leading-relaxed">
                  At 2ByaaG, our name reflects our mission—helping every student confidently say "I am..." when asked who they want to become. And we believe one of the most powerful ways to get there is through intergenerational storytelling.
                </p>
                <p className="text-lg leading-relaxed">
                  In today's fast-changing world, students are overwhelmed with choices but lack grounded guidance. At the same time, elderly mentors—retired engineers, doctors, artists, teachers—hold decades of lived experience and wisdom. 2ByaaG brings them together in a way that's natural, powerful, and deeply rooted in our culture.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div {...fadeIn} className="space-y-6">
            <h2 className="text-3xl font-semibold text-yellow-700">A Grandfather's Story. A Student's Future.</h2>
            <Card className="bg-white/80 backdrop-blur shadow-xl">
              <CardContent className="p-6">
                <p className="text-lg leading-relaxed">
                  Imagine a student in a small town who has only heard of four careers: doctor, engineer, lawyer, teacher. She meets a retired journalist who once covered war zones, local elections, and social movements. As he shares his story—his fears, his triumphs, and how writing gave him purpose—her world expands. She realizes her love for writing could also become a career. Suddenly, she has a direction. A role model. A path.
                </p>
                <p className="mt-4 text-lg font-semibold text-yellow-800">
                  This isn't hypothetical. These are the kinds of stories we're collecting through 2ByaaG.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div {...fadeIn} className="space-y-6">
            <h2 className="text-3xl font-semibold text-yellow-700">The Power of Storytelling—for All Generations</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-white/80 backdrop-blur shadow-xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-yellow-600">For Elderly Mentors</h3>
                  <p>Storytelling offers purpose. After retirement, many seniors feel disconnected. At 2ByaaG, they become mentors, guides, and wisdom-keepers. Studies show that elderly mentors involved in community storytelling report higher life satisfaction, reduced loneliness, and improved mental well-being.</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/80 backdrop-blur shadow-xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-yellow-600">For Students</h3>
                  <p>These stories make careers real. They don't just hear, "You should become a doctor." They hear, "I became a doctor during a time when opportunities were few. I made tough choices, and here's what I learned." Students connect emotionally, ask better questions, and feel more confident navigating their future.</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/80 backdrop-blur shadow-xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-yellow-600">For Parents</h3>
                  <p>Especially in India where career decisions are often family-driven, hearing a respected elder share the viability of a less conventional path—say, design or entrepreneurship—can be a game-changer. Suddenly, they're open. Curious. Supportive.</p>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          <motion.div {...fadeIn} className="space-y-6">
            <h2 className="text-3xl font-semibold text-yellow-700">Making It Work in Today's World</h2>
            <Card className="bg-white/80 backdrop-blur shadow-xl">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <p className="text-lg">At 2ByaaG, we've designed a system that works online and offline:</p>
                  <ul className="list-disc pl-6 space-y-3">
                    <li><strong>Career Story Libraries:</strong> Where mentors upload videos or written reflections on their career journeys.</li>
                    <li><strong>Parent-Elder Dialogues:</strong> Conversations that bridge experience with concern.</li>
                    <li><strong>Live Storytelling Events:</strong> Hosted in schools and community centers, bringing everyone into the same room.</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div {...fadeIn} className="space-y-6">
            <h2 className="text-3xl font-semibold text-yellow-700">Because the Need Is Urgent</h2>
            <Card className="bg-white/80 backdrop-blur shadow-xl">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <ul className="space-y-4 text-lg">
                    <li className="flex items-center gap-2">
                      <span className="text-yellow-600 font-bold">90%</span> of Indian students risk choosing careers without enough guidance.
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-yellow-600 font-bold">50%</span> of elderly adults in India report feeling isolated.
                    </li>
                    <li>Cultural values are shifting, but respect for elders remains strong.</li>
                  </ul>
                  <p className="text-lg mt-6">
                    2ByaaG isn't just about guidance. It's about connection, confidence, and community. It's about reviving something we've always known—stories shape lives.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div {...fadeIn} className="text-center py-8">
            <p className="text-xl font-semibold text-yellow-800 mb-6">
              Whether you're a student searching for direction, a parent seeking clarity, or a mentor ready to give back—there's a place for you here.
            </p>
            <p className="text-2xl font-bold text-yellow-900">
              Because every story told is a future shaped.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}